/* ============================================================================

Movatic WMS — Frontend | CesarFilho 932048016

Arquivo: src/pages/assembly/AssemblyLinksPage.tsx

Assembly • Ligações de montagem

Lista relações de montagem entre Assets (parent → child).

Filtros: estado (ativas / histórico / todas) + pesquisa livre.

Apenas leitura por enquanto.

Todas as chamadas para /api/assembly/links/ enviam:

Authorization: Bearer <token> (via useAuthStore)

credentials: "include" (cookies de sessão, se houver)

========================================================================== */

import { useEffect, useState, type FC } from "react";
import { useAuthStore } from "@/store/authStore";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

/* -------------------------------------------------------------------------- */
/* Tipos e helpers                                                            */
/* -------------------------------------------------------------------------- */

type LinkStateFilter = "active" | "inactive" | "all";

type AssemblyLink = {
    id: number;

    parent_label: string;
    child_label: string;

    relation_type: string;
    relation_type_label: string;

    valid_from: string;
    valid_to: string | null;

    note: string | null;
};

/** Base da API (usa VITE_API_BASE_URL ou /api). */
const API_BASE =
    (import.meta as any).env.VITE_API_BASE_URL?.replace(/\/$/, "") || "/api";
const ASM_BASE = `${API_BASE}/assembly`;

/** Monta headers com Authorization (quando existir token). */
function buildAuthHeaders(): Record<string, string> {
    const state: any = (useAuthStore as any)?.getState?.() ?? {};
    const token: string | undefined = state.accessToken;

    const headers: Record<string, string> = {};
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
}

/** Formata data/hora ISO para “dd/MM/yyyy HH:mm”. */
function formatDateTime(iso?: string | null): string {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

/* -------------------------------------------------------------------------- */
/* Componente principal                                                       */
/* -------------------------------------------------------------------------- */

const AssemblyLinksPage: FC = () => {
    const [stateFilter, setStateFilter] = useState<LinkStateFilter>("active");
    const [search, setSearch] = useState<string>("");

    const [items, setItems] = useState<AssemblyLink[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(
        "Em breve: árvore de montagem e histórico de substituição de componentes (variador → quadro → máquina → cliente). Por agora, a página já permite consultar as ligações registadas."
    );

    async function loadLinks(): Promise<void> {
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams();

            if (stateFilter) {
                params.append("state", stateFilter);
            }
            if (search.trim()) {
                params.append("q", search.trim());
            }

            const url = `${ASM_BASE}/links/?${params.toString()}`;

            const res = await fetch(url, {
                method: "GET",
                headers: buildAuthHeaders(), // Authorization: Bearer <token>
                credentials: "include", // cookies de sessão Django (se houver)
            });

            if (!res.ok) {
                let detail = "";
                try {
                    const data = await res.json();
                    if (data && typeof data === "object" && "detail" in data) {
                        detail = ` ${JSON.stringify(data)}`;
                    }
                } catch {
                    /* ignore */
                }
                setError(
                    `Erro ao carregar ligações de montagem (HTTP ${res.status}).${detail}`
                );
                setItems([]);
                setInfo(null);
                return;
            }

            const data = await res.json();

            let results: AssemblyLink[] = [];
            if (Array.isArray(data)) {
                results = data as AssemblyLink[];
            } else if (Array.isArray((data as any)?.results)) {
                results = (data as any).results as AssemblyLink[];
            }

            setItems(results);

            if (results.length === 0) {
                setInfo(
                    "Nenhuma ligação encontrada para o estado/pesquisa atual."
                );
            } else {
                setInfo(null);
            }
        } catch (e) {
            console.error(e);
            setError(
                "Erro de comunicação com o backend ao carregar ligações de montagem."
            );
            setItems([]);
            setInfo(null);
        } finally {
            setLoading(false);
        }
    }

    // Carrega inicialmente e sempre que o filtro de estado muda
    useEffect(() => {
        void loadLinks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateFilter]);

    function handleResetFilters() {
        setStateFilter("active");
        setSearch("");
        void loadLinks();
    }

    /* ------------------------------- RENDER -------------------------------- */

    return (
        <div className="p-4 lg:px-6 lg:py-6 space-y-6">
            <header className="space-y-1">
                <h1 className="text-2xl font-semibold">
                    Assembly • Ligações de montagem
                </h1>
                <p className="text-sm text-slate-300">
                    Consulta das relações entre componentes (parent → child) com
                    informação de período de validade e notas de montagem/
                    substituição. Isto permite rastrear, por exemplo, qual
                    variador está montado em que quadro, que máquina e em que
                    cliente.
                </p>
            </header>

            {/* Mensagens de estado */}
            {error && (
                <div className="rounded-md border border-red-500/60 bg-red-500/10 px-4 py-2 text-sm text-red-100">
                    {error}
                </div>
            )}
            {info && !error && (
                <div className="rounded-md border border-sky-500/60 bg-sky-500/10 px-4 py-2 text-sm text-sky-100">
                    {info}
                </div>
            )}

            {/* Filtros */}
            <section className="space-y-3 rounded-lg border border-slate-800 bg-slate-950/40 px-4 py-3">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="space-y-1">
                        <label
                            className="text-xs font-medium"
                            htmlFor="asm-links-state"
                        >
                            Estado
                        </label>
                        <select
                            id="asm-links-state"
                            value={stateFilter}
                            onChange={(e) =>
                                setStateFilter(e.target.value as LinkStateFilter)
                            }
                            className="w-40 rounded-md border border-slate-700 bg-slate-950/60 px-2 py-1 text-sm"
                        >
                            <option value="active">Ativas</option>
                            <option value="inactive">Histórico</option>
                            <option value="all">Todas</option>
                        </select>
                    </div>

                    <div className="flex-1 min-w-[240px] space-y-1">
                        <label
                            className="text-xs font-medium"
                            htmlFor="asm-links-search"
                        >
                            Pesquisa
                        </label>
                        <Input
                            id="asm-links-search"
                            type="text"
                            placeholder="Produto ou nº de série (parent/child)…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <div className="flex items-end gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleResetFilters}
                        >
                            Limpar filtros
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            onClick={() => void loadLinks()}
                            disabled={loading}
                        >
                            {loading ? "A atualizar..." : "Atualizar"}
                        </Button>
                    </div>
                </div>
            </section>

            {/* Tabela de ligações */}
            <div className="border border-slate-800 rounded-lg overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[260px]">
                                Parent (onde está montado)
                            </TableHead>
                            <TableHead className="w-[260px]">
                                Child (componente)
                            </TableHead>
                            <TableHead className="w-[140px]">Tipo</TableHead>
                            <TableHead className="w-[220px]">Período</TableHead>
                            <TableHead>Nota</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell className="text-sm text-slate-300">
                                    A carregar ligações de montagem...
                                </TableCell>
                                <TableCell />
                                <TableCell />
                                <TableCell />
                                <TableCell />
                            </TableRow>
                        ) : items.length === 0 ? (
                            <TableRow>
                                <TableCell className="text-sm text-slate-300">
                                    Nenhuma ligação encontrada para o
                                    estado/pesquisa atual.
                                </TableCell>
                                <TableCell />
                                <TableCell />
                                <TableCell />
                                <TableCell />
                            </TableRow>
                        ) : (
                            items.map((link) => (
                                <TableRow key={link.id}>
                                    <TableCell className="text-sm">
                                        {link.parent_label}
                                    </TableCell>
                                    <TableCell className="text-sm">
                                        {link.child_label}
                                    </TableCell>
                                    <TableCell>
                                        <span className="inline-flex items-center rounded-full border border-emerald-500/60 bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-100">
                                            {link.relation_type_label ||
                                                link.relation_type}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-sm">
                                        {formatDateTime(link.valid_from)} {" → "}
                                        {formatDateTime(link.valid_to)}
                                    </TableCell>
                                    <TableCell className="text-sm text-slate-200">
                                        {link.note || (
                                            <span className="text-slate-500">
                                                —
                                            </span>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
};

export default AssemblyLinksPage;